/**
 * Permission Utility Functions
 * 
 * This utility provides centralized permission checking functionality
 * for the Vidhyantra application. It extracts permissions directly from
 * JWT tokens (uiPermissions and contextToken) and provides easy-to-use
 * functions for checking user permissions.
 */

import { jwtDecode } from 'jwt-decode'
import { hasPermission } from '@/core/permissions/permissionResolver'

/**
 * Decode JWT token and extract payload
 * Note: This only decodes the token, it does NOT verify the signature
 * Verification happens on the backend. Frontend just reads the payload.
 */
export const decodeJWT = (token: string | null): any => {
  try {
    if (!token) {
      return null
    }
    
    const decoded = jwtDecode(token)
    return decoded
  } catch (error: any) {
    return null
  }
}

// getUserPermissions() has been removed - use usePermissions() hook instead

/**
 * Get user type from Redux state only (backend-provided)
 */
export const getUserType = (): string | null => {
  try {
    const store = (window as any).__REDUX_STORE__
    if (store) {
      const state = store.getState()
      if (state?.auth?.user?.userType) {
        return state.auth.user.userType
      }
    }
    return null
  } catch (error) {
    return null
  }
}

/**
 * Get context type from JWT token, URL, or stored context
 * Priority: JWT tokens > URL parsing > Stored context
 */
export const getContextType = (): string | null => {
  try {
    // Only use Redux state (backend-provided) for context type.
    try {
      const store = (window as any).__REDUX_STORE__
      if (store) {
        const state = store.getState()
        const currentContextType = state?.auth?.currentContext?.type
        if (currentContextType) {
          const contextTypeMap: Record<string, string> = {
            'PLATFORM': 'platform',
            'ORGANIZATION': 'organization',
            'BRANCH': 'branch',
          }
          return contextTypeMap[currentContextType] || null
        }
      }
    } catch (reduxError) {
      // Fall through to null
    }

    return null
  } catch (error) {
    return null
  }
}

/**
 * Check if user has permission for a specific action on a resource
 */
function getReduxPermissions(): string[] {
  try {
    const store = (window as any).__REDUX_STORE__
    const perms = store?.getState()?.permissions?.permissions
    return Array.isArray(perms) ? perms : []
  } catch {
    return []
  }
}

export const hasResourcePermission = (resource: string, action: string, context: string | null = null): boolean => {
  try {
    const currentContext = context || getContextType()
    if (!currentContext) {
      return false
    }

    const permission = `${currentContext}.${resource}.${action}`
    return hasPermission(permission, getReduxPermissions())
  } catch (error) {
    return false
  }
}

/**
 * Get permissions for a specific resource
 */
export const getResourcePermissions = (resource: string, context: string | null = null): Record<string, boolean> => {
  try {
    const currentContext = context || getContextType()
    if (!currentContext) {
      return { view: false, create: false, edit: false, delete: false }
    }
    
    const actions = ['view', 'create', 'edit', 'delete']
    const permissions: Record<string, boolean> = {}
    
    const grantList = getReduxPermissions()
    actions.forEach(action => {
      const permission = `${currentContext}.${resource}.${action}`
      permissions[action] = hasPermission(permission, grantList)
    })
    
    return permissions
  } catch (error) {
    return { view: false, create: false, edit: false, delete: false }
  }
}

/**
 * Check if user is a specific user type
 */
export const isUserType = (userType: string): boolean => {
  try {
    const currentUserType = getUserType()
    return currentUserType === userType
  } catch (error) {
    return false
  }
}

/**
 * Check if user is in a specific context
 */
export const isInContext = (context: string): boolean => {
  try {
    const currentContext = getContextType()
    return currentContext === context
  } catch (error) {
    return false
  }
}

/**
 * Get all token information (for debugging)
 * Note: Tokens are in httpOnly cookies, not accessible from frontend
 * @deprecated Use usePermissions() hook for permission data
 */
export const getTokenInfo = () => {
  return {
    tokens: {
      note: 'Tokens are in httpOnly cookies, not accessible from frontend'
    },
    extracted: {
      permissions: [],
      userType: getUserType(),
      contextType: getContextType(),
      permissionsCount: 0
    }
  }
}

/**
 * Get all available permissions (for debugging)
 * @deprecated Use usePermissions() hook instead
 */
export const getPermissionInfo = () => {
  return {
    permissions: [],
    userType: getUserType(),
    contextType: getContextType(),
    permissionsCount: 0
  }
}

// Common permission patterns for easy use
export const PERMISSION_PATTERNS = {} as const

// User types
export const USER_TYPES = {
  PLATFORM_USER: 'PLATFORM_USER',
  ORG_USER: 'ORG_USER',
  BRANCH_USER: 'BRANCH_USER'
} as const

// Context types
export const CONTEXT_TYPES = {
  PLATFORM: 'platform',
  ORGANIZATION: 'organization',
  BRANCH: 'branch'
} as const

/**
 * Get active branch permissions from Redux state only (backend-provided)
 * Returns permissions for the currently active branch
 * @deprecated Use usePermissions() hook instead
 */
export const getActiveBranchPermissions = (): string[] => {
  return []
}

/**
 * Get all accessible branches from Redux state only (backend-provided)
 * Returns array of branch IDs that user has access to
 */
export const getAllAccessibleBranches = (): string[] => {
  try {
    const store = (window as any).__REDUX_STORE__
    if (store) {
      const state = store.getState()
      if (state?.auth?.contextData?.branch_ids && Array.isArray(state.auth.contextData.branch_ids)) {
        return state.auth.contextData.branch_ids
      }
    }
    return []
  } catch (error) {
    return []
  }
}

/**
 * Get active branch ID from Redux state only (backend-provided)
 */
export const getActiveBranchId = (): string | null => {
  try {
    const store = (window as any).__REDUX_STORE__
    if (store) {
      const state = store.getState()
      if (state?.auth?.contextData?.active_branch_id) {
        return state.auth.contextData.active_branch_id
      }
    }
    return null
  } catch (error) {
    return null
  }
}

/**
 * Get role for a specific branch from Redux state only (backend-provided)
 */
export const getBranchRole = (branchId: string): string | null => {
  try {
    const store = (window as any).__REDUX_STORE__
    if (store) {
      const state = store.getState()
      if (state?.auth?.contextData?.roles && typeof state.auth.contextData.roles === 'object') {
        const branchRole = state.auth.contextData.roles[branchId]
        if (branchRole && typeof branchRole === 'string') {
          return branchRole
        }
      }
    }
    return null
  } catch (error) {
    return null
  }
}

/**
 * Get permissions for a specific branch (backend-provided only)
 * Note: Only active branch permissions are available from backend
 * @deprecated Use usePermissions() hook instead
 */
export const getBranchPermissions = (_branchId: string): string[] => {
  return []
}

/**
 * Get active branch role from Redux state
 */
export const getActiveBranchRole = (): string | null => {
  try {
    const activeBranchId = getActiveBranchId()
    if (!activeBranchId) {
      return null
    }
    
    return getBranchRole(activeBranchId)
  } catch (error) {
    return null
  }
}

